import * as React from 'react';
import { useState } from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { commonStyleSheet } from 'library/common-styles';
import { useTranslation } from 'react-i18next';
import {
    Row,
    FormInstance,
    Button,
} from 'antd';
import { getPieceLevelFieldFromMapping } from '../generic-sub-layout-renderer';

const styles = (theme: ThemeType) => ({
    referenceItem: {
        display: 'flex',
        flexDirection: 'column',
    },
    referenceTitle: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
    },
    contentType: {
        ...commonStyleSheet(theme).flexRow,
        justifyContent: 'space-between',
        '& .ant-form-item': {
            marginBottom: 0,
        },
    },
    addMoreButton: {
        margin: '10px 0',
    },
    pieceItem: {
        marginBottom: '5px',
        padding: '10px',
        backgroundColor: '#fafafa',
        borderRadius: '5px',
        width: '100%',
    },
    pieceItemHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    pieceItemHeaderTitle: {
        fontSize: 15,
        paddingTop: 10,
        paddingBottom: 10,
    },
    pieceFieldContainer: {
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
    },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReturnType<typeof mapStateToProps> {
    form: FormInstance;
    params: any;
}

const PieceField = (props: IProps) => {
    const [itemIndices, setItemIndices] = useState([[0]]);

    const { t } = useTranslation();
    const {
        form,
        params,
        classes,
    } = props;

    const pieceLayout = params?.subLayout?.piece || [];

    const {
        piecesData,
        setPiecesData,
        disabledFields = {},
        confirmSlotRefetchForPieceChange,
    } = params;
    const isPieceDisabled = !!disabledFields.piece;

    // Runs the piece mutation directly, or behind the slot-refetch confirmation when a slot is locked.
    const withSlotRefetchGuard = (proceed: () => void) => {
        if (typeof confirmSlotRefetchForPieceChange === 'function') {
            confirmSlotRefetchForPieceChange(proceed);
        } else {
            proceed();
        }
    };

    const addPieceRaw = () => {
        setPiecesData((currentPiecesData: any[]) => [...currentPiecesData, {}]);
    };

    const addPiece = () => {
        withSlotRefetchGuard(addPieceRaw);
    };

    React.useEffect(() => {
        if (piecesData?.length === 0) {
            addPieceRaw();
        }
    }, []);

    const handleRemovePiece = (index: number) => {
        if (piecesData.length <= 1) {
            return;
        }

        const performRemove = () => {
            const allFields = form.getFieldsValue();
            const updatedFields: any = {};
            const fieldsToRemove: string[] = [];

            const pieceFieldKeys = Object.keys(allFields).filter((key) => key.startsWith('piece_') && key.match(/_(\d+)$/));

            pieceFieldKeys.forEach((fieldKey) => {
                const match = fieldKey.match(/^(.+)_(\d+)$/);
                if (!match) return;

                const [, fieldName, fieldIndex] = match;
                const currentIndex = parseInt(fieldIndex, 10);

                if (currentIndex === index) {
                    fieldsToRemove.push(fieldKey);
                } else if (currentIndex > index) {
                    const newIndex = currentIndex - 1;
                    const newFieldKey = `${fieldName}_${newIndex}`;
                    updatedFields[newFieldKey] = allFields[fieldKey];
                    fieldsToRemove.push(fieldKey);
                }
            });

            fieldsToRemove.forEach((key) => {
                form.resetFields([key]);
            });

            if (Object.keys(updatedFields).length > 0) {
                form.setFieldsValue(updatedFields);
            }

            const newPiecesData = [...piecesData];
            newPiecesData.splice(index, 1);
            setPiecesData(newPiecesData);
        };

        withSlotRefetchGuard(performRemove);
    };

    const renderAddMoreButton = () => {
        return (
            <Button
                type="primary"
                onClick={addPiece}
                className={classes.addMoreButton}
                disabled={isPieceDisabled}
            >
                {t('add_more_text')}
            </Button>
        );
    };


    const renderRemovePiece = (index: number) => {
        return (
            <Button danger type="link" onClick={() => handleRemovePiece(index)} disabled={isPieceDisabled}>
                {t('remove')}
            </Button>
        );
    };

    const pieceComponent = (
        <>
            {piecesData?.map((piece: any, pieceIndex: number) => {
                const pieceForm = pieceLayout?.map(
                    (fieldKey: any) => getPieceLevelFieldFromMapping(fieldKey, form, {
                        ...params,
                        itemIndices,
                        setItemIndices,
                        id: pieceIndex,
                        key: `piece_${fieldKey}_${pieceIndex}`,
                        pieceIndex,
                    }),
                );
                return (
                    <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`piece-${pieceIndex}`}
                        className={classes.pieceItem}
                    >
                        <div className={classes.pieceItemHeader}>
                            <div className={classes.pieceItemHeaderTitle}>
                                {'Piece '}
                                {pieceIndex + 1}
                            </div>
                            {piecesData?.length !== 1 && renderRemovePiece(pieceIndex)}
                        </div>
                        <Row gutter={20}>
                            {pieceForm}
                        </Row>
                    </div>
                );
            })}
        </>
    );

    return (
        <>
            {pieceLayout?.length > 0 && (
                <div className={classes.pieceFieldContainer}>
                    {pieceComponent}
                    {renderAddMoreButton()}
                </div>
            )}
        </>
    );
};

const mapStateToProps = () => ({});

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(PieceField);
